import { defineStore } from 'pinia'
import { convertArrayToObject } from 'src/utils'

export const useGiftCardStore = defineStore('giftCard', {
  state: () => ({
    myActiveGiftCards: {},
    selGiftCardForCheckout: {},
    selectedExternalGiftCardForCheckout: {},
    useGiftCardForCheckout: false,
    isCurrencyInputFocused: false,
    giftCards: [],
    loading: false,
    detailLoading: false,
    giftCardDetails: {},
  }),
  getters: {
    isLoading: (state) => state.loading,
    giftCardDetailByID(state) {
      return (id) => state.giftCardDetails[id] || null
    },
    selectedGiftCardForCheckout() {
      const selected = this.selGiftCardForCheckout || {}
      return Object.keys(selected).length
        ? selected // Return selected if not empty
        : Object.fromEntries(Object.entries(this.myActiveGiftCards).slice(0, 2)) // Otherwise get first 2 active cards
    },
    totalRedeemAmount() {
      const allCards = [
        ...Object.values(this.selectedGiftCardForCheckout),
        ...Object.values(this.selectedExternalGiftCardForCheckout),
      ]

      const redeemAmount = allCards.reduce(
        (total, card) => total + (Number(card.redeemAmount) || 0),
        0
      )
      return Number(redeemAmount.toFixed(2)) || 0
    },
  },
  actions: {
    async getAllActiveGiftCards() {
      try {
        const { data } = await this.$axiosInstance.get('/GiftCard/MyGiftCard', {
          params: {
            disablePaging: true,
            isActivated: true,
            filters: '(Status)@=1',
            global: true,
          },
        })

        const activeGiftCards = convertArrayToObject(
          data.data.items.filter((item) => item.availableBalance > 0),
          'id'
        )
        this.myActiveGiftCards = activeGiftCards
      } catch (error) {
        // Handle error appropriately
      }
    },
    async findGiftCardByNumber(params) {
      try {
        const {
          data: { data },
        } = await this.$axiosInstance.get('/giftCard/getGiftCardDetails', {
          params,
        })

        return data
      } catch (error) {
        throw error
      }
    },
    async getGiftCards(params = {}) {
      try {
        const isFirstPage = params.page === 1

        if (isFirstPage) this.loading = true

        const {
          data: {
            data: { items = [], hasNext },
          },
        } = await this.$axiosInstance.get('/giftCard', { params })

        const giftCards = isFirstPage
          ? items
          : [...(this.giftCards || []), ...items]
        this.giftCards = giftCards

        return hasNext
      } catch {
        return false
      } finally {
        this.loading = false
      }
    },
    async getMyGiftCards(params) {
      try {
        this.loading = true
        let { data } = await this.$axiosInstance.get('/GiftCard/MyGiftCard', {
          params,
        })
        if (data.data) {
          this.myGiftCards = data.data.items
        }
        return data.data
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    async sendGiftCard({ params, queryParams }) {
      try {
        const response = await this.$axiosInstance.post(
          '/GiftCard/SendGiftCardViaMail',
          params,
          { params: queryParams }
        )
        return response.data
      } catch (error) {
      } finally {
      }
    },
    async activeGiftCard(params) {
      const { myGiftCardId, ActionForStatus, isGuestUser } = params
      const response = await this.$axiosInstance.put(
        `/GiftCard/UpdateGiftCardStatus/${myGiftCardId}`,
        { ActionForStatus, isGuestUser }
      )
      return response.data
    },
    async retractGiftCard(params) {
      const { myGiftCardId, ActionForStatus, isGuestUser } = params
      const response = await this.$axiosInstance.put(
        `/GiftCard/UpdateGiftCardStatus/${myGiftCardId}`,
        { ActionForStatus, isGuestUser }
      )
      return response.data
    },
    async revokeGiftCard(params) {
      const { myGiftCardId, ActionForStatus, isGuestUser } = params
      const response = await this.$axiosInstance.put(
        `/GiftCard/UpdateGiftCardStatus/${myGiftCardId}`,
        { ActionForStatus, isGuestUser }
      )
      return response.data
    },
    async getGiftCardDetails(giftCardId) {
      this.detailLoading = true

      try {
        const {
          data: { data: giftCardData },
        } = await this.$axiosInstance.get(`/GiftCard/${giftCardId}`)

        this.giftCardDetails = {
          ...this.giftCardDetails,
          [giftCardId]: {
            ...giftCardData,
            products: convertArrayToObject(
              giftCardData.products,
              'productType'
            ),
          },
        }

        return giftCardData
      } catch {
        return false
      } finally {
        this.detailLoading = false
      }
    },
    async claimGiftCard(params) {
      const { myGiftCardId, actionForStatus, isGuestUser } = params
      const response = await this.$axiosInstance.put(
        `/GiftCard/UpdateGiftCardStatus/${myGiftCardId}`,
        { actionForStatus, myGiftCardId, isGuestUser }
      )
      return response.data
    },
    async getGiftCardBalance(giftCardNumber) {
      try {
        let response = await this.$axiosInstance.get(
          `GiftCard/CheckGiftCardBalance/${giftCardNumber}`
        )
        return response.data
      } catch (error) {
        console.log('error', error)
      }
    },
    async findGiftCard(params) {
      try {
        const response = await this.$axiosInstance.post(
          '/GiftCard/FindGiftCard',
          params
        )
        return response.data
      } catch (error) {
        return error
      }
    },
    setField({ key, value }) {
      if (key) this[key] = value
    },
    setState(newData) {
      // Object.assign(state, newData)
      for (let key in newData) {
        this[key] = newData[key]
      }
    },
    removeExternalGiftCardByID({ giftCardID }) {
      delete this.selectedExternalGiftCardForCheckout[giftCardID]
    },
  },
})
